import axios from 'axios';
import { API_URL } from '../utils/apiConfig';

const API = API_URL;

/**
 * Register a new rider
 * @param {Object} userData - Rider registration data
 * @returns {Promise<Object>} - Registered user data
 */
export const registerRider = async (userData) => {
  try {
    const response = await axios.post(`${API}/auth/register/rider`, {
      phone: userData.phone,
      country_code: userData.country_code || '+265',
      first_name: userData.firstName,
      middle_name: userData.middleName,
      last_name: userData.lastName,
      email: userData.email,
      username: userData.username,
      password: userData.password,
      role: 'rider'
    });
    
    return {
      success: true,
      user: response.data
    };
  } catch (error) {
    console.error('Register rider error:', error.response?.data || error.message);
    return {
      success: false,
      message: error.response?.data?.detail || 'Failed to register. Please try again.'
    };
  }
};

/**
 * Register a new driver with vehicle information
 * @param {Object} userData - Driver and vehicle data
 * @returns {Promise<Object>} - Registered driver data
 */
export const registerDriver = async (userData) => {
  try {
    const formData = new FormData();
    
    // User data
    formData.append('phone', userData.phone);
    formData.append('country_code', userData.country_code || '+265');
    formData.append('first_name', userData.firstName);
    formData.append('last_name', userData.lastName);
    if (userData.middleName) formData.append('middle_name', userData.middleName);
    if (userData.email) formData.append('email', userData.email);
    formData.append('username', userData.username);
    formData.append('password', userData.password);
    
    // Driver type (vehicle or bike)
    const driverType = userData.driver_type || 'vehicle';
    console.log('Registering driver with type:', driverType, 'from userData.driver_type:', userData.driver_type);
    formData.append('driver_type', driverType);
    
    // Driver face photo (mandatory for all drivers)
    if (userData.facePhoto) {
      if (typeof userData.facePhoto === 'string' && userData.facePhoto.startsWith('data:image')) {
        const blob = await fetch(userData.facePhoto).then(r => r.blob());
        formData.append('driver_face', blob, 'driver_face.jpg');
      }
    }
    
    // Vehicle/Bike data
    if (userData.vehicle) {
      // Driver type already determined above
      
      // Always append plate (required for both)
      formData.append('vehicle_plate', userData.vehicle.plate);
      
      // Conditional fields based on driver type
      if (driverType === 'vehicle') {
        // Full vehicle data
        formData.append('vehicle_make', userData.vehicle.make);
        formData.append('vehicle_model', userData.vehicle.model);
        formData.append('vehicle_color', userData.vehicle.color);
        if (userData.vehicle.year) formData.append('vehicle_year', userData.vehicle.year);
        if (userData.vehicle.category) formData.append('vehicle_category', userData.vehicle.category);
        
        // Vehicle images - convert base64 to Blob
        if (userData.vehicle.images) {
          for (const [position, imageData] of Object.entries(userData.vehicle.images)) {
            if (imageData) {
              // Handle base64 data URLs (from Capacitor Camera or file input)
              if (typeof imageData === 'string') {
                if (imageData.startsWith('data:image')) {
                  // Base64 data URL
                  const blob = await fetch(imageData).then(r => r.blob());
                  formData.append(`vehicle_${position}`, blob, `vehicle_${position}.jpg`);
                } else if (imageData.startsWith('blob:')) {
                  // Blob URL
                  const blob = await fetch(imageData).then(r => r.blob());
                  formData.append(`vehicle_${position}`, blob, `vehicle_${position}.jpg`);
                }
              } else if (imageData instanceof Blob || imageData instanceof File) {
                // Direct Blob/File object
                formData.append(`vehicle_${position}`, imageData, `vehicle_${position}.jpg`);
              }
            }
          }
        }
      } else if (driverType === 'bike') {
        // Optional bike data
        if (userData.vehicle.make) formData.append('vehicle_make', userData.vehicle.make);
        if (userData.vehicle.model) formData.append('vehicle_model', userData.vehicle.model);
        if (userData.vehicle.color) formData.append('vehicle_color', userData.vehicle.color);
        if (userData.vehicle.category) formData.append('vehicle_category', userData.vehicle.category || 'bike');
        // No images, no year for bikes
      }
    }
    
    const response = await axios.post(`${API}/auth/register/driver`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return {
      success: true,
      user: response.data.user,
      vehicle: response.data.vehicle
    };
  } catch (error) {
    console.error('Register driver error:', error.response?.data || error.message);
    return {
      success: false,
      message: error.response?.data?.detail || 'Failed to register. Please try again.'
    };
  }
};

/**
 * Get user by ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - User data
 */
export const getUserById = async (userId) => {
  try {
    const response = await axios.get(`${API}/users/${userId}`);
    return {
      success: true,
      user: response.data
    };
  } catch (error) {
    console.error('Get user error:', error.response?.data || error.message);
    return {
      success: false,
      message: error.response?.data?.detail || 'Failed to fetch user data.'
    };
  }
};

/**
 * Get user by phone number
 * @param {string} phone - Phone number
 * @param {string} countryCode - Country code (default: +265)
 * @returns {Promise<Object>} - User data
 */
export const getUserByPhone = async (phone, countryCode = '+265') => {
  try {
    const response = await axios.get(`${API}/users/phone/${phone}`, {
      params: { country_code: countryCode }
    });
    return {
      success: true,
      user: response.data
    };
  } catch (error) {
    console.error('Get user error:', error.response?.data || error.message);
    return {
      success: false,
      message: error.response?.data?.detail || 'Failed to fetch user data.'
    };
  }
};

export default {
  registerRider,
  registerDriver,
  getUserById,
  getUserByPhone
};
